import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SurvivalPriority {
  name: string;
  timeLimit: string;
  description: string;
  icon: string;
  color: string;
}

interface NavigationMethod {
  name: string;
  accuracy: string;
  condition: string;
  icon: string;
  color: string;
}

interface EmergencySignal {
  name: string;
  range: string;
  method: string;
  color: string;
}

interface SurvivalFact {
  label: string;
  icon: string;
}

interface SurvivalSkillsTemplateProps {
  topic: string;
  subtitle: string;
  priorities: SurvivalPriority[];
  navigation: NavigationMethod[];
  signals: EmergencySignal[];
  survivalFacts: SurvivalFact[];
  survivalRuleOfThrees: { label: string; time: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: SurvivalSkillsTemplateProps = {
  topic: '野外生存技巧',
  subtitle: 'Wilderness Survival Skills',
  priorities: [
    {
      name: '庇护所',
      timeLimit: '3小时',
      description: '极端天气下人体失温的致命时间',
      icon: '⛺',
      color: '#84cc16',
    },
    {
      name: '水源',
      timeLimit: '3天',
      description: '人体脱水后生存的极限天数',
      icon: '💧',
      color: '#22d3ee',
    },
    {
      name: '火种',
      timeLimit: '关键',
      description: '取暖、净水、烹饪、求救信号',
      icon: '🔥',
      color: '#f97316',
    },
    {
      name: '食物',
      timeLimit: '3周',
      description: '人体不进食的极限生存时间',
      icon: '🍖',
      color: '#a3e635',
    },
  ],
  navigation: [
    {
      name: '太阳定向',
      accuracy: '±15°',
      condition: '晴天白天',
      icon: '☀️',
      color: '#fbbf24',
    },
    {
      name: '北极星定位',
      accuracy: '±1°',
      condition: '晴朗夜晚',
      icon: '⭐',
      color: '#e2e8f0',
    },
    {
      name: '地形判断',
      accuracy: '±30°',
      condition: '有参照物',
      icon: '🏔️',
      color: '#94a3b8',
    },
    {
      name: '指南针使用',
      accuracy: '±2°',
      condition: '携带指南针',
      icon: '🧭',
      color: '#f87171',
    },
  ],
  signals: [
    { name: 'SOS信号', range: '远距离', method: '三短三长三短', color: '#f87171' },
    { name: '镜面反光', range: '15-30km', method: '信号镜/光滑金属', color: '#fbbf24' },
    { name: '烟火信号', range: '5-10km', method: '湿木+绿叶产浓烟', color: '#94a3b8' },
    { name: '哨声求救', range: '1-2km', method: '三声连续短哨', color: '#84cc16' },
  ],
  survivalFacts: [
    { label: '绳结技术', icon: '🪢' },
    { label: '可食植物', icon: '🌿' },
    { label: '急救处置', icon: '🩹' },
    { label: '心理韧性', icon: '🧠' },
  ],
  survivalRuleOfThrees: [
    { label: '无空气', time: '3分钟' },
    { label: '极端温度', time: '3小时' },
    { label: '无饮水', time: '3天' },
    { label: '无食物', time: '3周' },
  ],
  stats: [
    { value: '72小时', label: '黄金救援期' },
    { value: '3-3-3', label: '生存法则(分钟/小时/天)' },
    { value: '80%', label: '迷路者原地打转概率' },
  ],
  bottomQuote:
    '最好的生存技巧是永远不需要使用它们——但当你真正需要时，知识就是生与死之间的距离',
};

export const SurvivalSkillsTemplate: React.FC<
  Partial<SurvivalSkillsTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const priorityStart = 30;
  const navStart = 160;
  const compassStart = 200;
  const signalStart = 280;
  const factStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const compassDraw = interpolate(
    frame,
    [compassStart, compassStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const needleAngle = interpolate(
    frame,
    [compassStart, compassStart + 60, compassStart + 80],
    [90, -10, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a2e05 0%, #0a1502 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating ember/spark particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const baseY = (i * 173) % 1080;
        const size = 2 + (i % 4);
        const rise = -(frame * 0.3 + i * 20) % 1080;
        const sway = Math.sin(frame * 0.03 + i) * 15;
        const pulse = 0.05 + 0.06 * Math.sin(frame * 0.06 + i * 0.9);
        const colors = ['#f97316', '#fbbf24', '#84cc16', '#ef4444'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + sway,
              top: ((baseY + rise) % 1080 + 1080) % 1080,
              width: size,
              height: size,
              borderRadius: i % 3 === 0 ? '50%' : '2px',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}50`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#3f6212',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#ecfccb',
            flexShrink: 0,
          }}
        >
          🏕️ 生存科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ecfccb',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#7a8a60',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Survival Priorities ===== */}
      <div style={{ position: 'absolute', top: 130, left: 36, width: 420 }}>
        <div
          style={{
            fontSize: 26,
            color: '#7a8a60',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          生存优先级 · 3-3-3法则
        </div>
        <svg width="400" height="520" viewBox="0 0 400 520">
          <defs>
            <linearGradient id="survGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#84cc16" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#3f6212" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          <rect
            x="10"
            y="10"
            width="380"
            height="500"
            rx="12"
            fill="url(#survGrad)"
            strokeWidth="2"
            stroke="#3f621240"
            opacity={interpolate(frame, [priorityStart, priorityStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {/* Priority pyramid structure */}
          {p.priorities.map((pr, i) => {
            const yPos = 30 + i * 120;
            const s = spring({
              frame: frame - (priorityStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const barW = 340 - i * 50;
            return (
              <g key={i} opacity={s}>
                {/* Priority level bar */}
                <rect
                  x={(400 - barW) / 2}
                  y={yPos}
                  width={barW * s}
                  height="6"
                  rx="3"
                  fill={pr.color}
                  opacity="0.5"
                />

                <text
                  x="55"
                  y={yPos + 34}
                  fill="#ecfccb"
                  fontSize="22"
                  fontWeight="800"
                >
                  {pr.icon} {pr.name}
                </text>
                <text
                  x="340"
                  y={yPos + 34}
                  fill={pr.color}
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="end"
                >
                  {pr.timeLimit}
                </text>
                <text x="55" y={yPos + 68} fill="#7a8a60" fontSize="20">
                  {pr.description}
                </text>

                {/* Urgency indicator dots */}
                {Array.from({ length: 4 - i }).map((_, d) => (
                  <circle
                    key={d}
                    cx={55 + d * 16}
                    cy={yPos + 90}
                    r="4"
                    fill={pr.color}
                    opacity="0.6"
                  />
                ))}
                <text x={55 + (4 - i) * 16 + 8} y={yPos + 96} fill="#7a8a60" fontSize="20">
                  紧迫度
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== CENTER: Navigation Methods + Compass SVG ===== */}
      <div style={{ position: 'absolute', top: 130, left: 480, width: 400 }}>
        <div
          style={{
            fontSize: 26,
            color: '#7a8a60',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          野外定向方法
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.navigation.map((nav, i) => {
            const s = spring({
              frame: frame - (navStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a2e0590',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${nav.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${nav.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${nav.color}30`,
                  }}
                >
                  {nav.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 24, fontWeight: 800, color: nav.color }}
                    >
                      {nav.name}
                    </span>
                    <span style={{ fontSize: 22, color: '#7a8a60' }}>
                      精度 {nav.accuracy}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 22, color: '#7a8a60', marginTop: 2 }}
                  >
                    适用条件: {nav.condition}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* ===== Compass Navigation Principle (right of navigation methods) ===== */}
      <div style={{ position: 'absolute', top: 130, left: 900, width: 580 }}>
        <div
          style={{
            fontSize: 26,
            color: '#7a8a60',
            marginBottom: 14,
            letterSpacing: '0.08em',
            fontWeight: 700,
          }}
        >
          指南针导航原理
        </div>
        <svg width="580" height="440" viewBox="0 0 580 440">
          {/* Compass circle */}
          <circle
            cx="180"
            cy="220"
            r="155"
            fill="none"
            stroke="#84cc16"
            strokeWidth="3"
            strokeDasharray={2 * Math.PI * 155}
            strokeDashoffset={2 * Math.PI * 155 * (1 - compassDraw)}
            opacity="0.6"
          />
          <circle
            cx="180"
            cy="220"
            r="130"
            fill="#1a2e05"
            stroke="#3f6212"
            strokeWidth="1.5"
            opacity={compassDraw}
          />

          {/* Cardinal directions */}
          {[
            { dir: 'N', angle: -90, color: '#f87171' },
            { dir: 'E', angle: 0, color: '#7a8a60' },
            { dir: 'S', angle: 90, color: '#7a8a60' },
            { dir: 'W', angle: 270, color: '#7a8a60' },
          ].map((cd, i) => {
            const rad = (cd.angle * Math.PI) / 180;
            return (
              <text
                key={i}
                x={180 + 143 * Math.cos(rad)}
                y={226 + 143 * Math.sin(rad)}
                fill={cd.color}
                fontSize="24"
                fontWeight="800"
                textAnchor="middle"
                opacity={compassDraw}
              >
                {cd.dir}
              </text>
            );
          })}

          {/* Needle */}
          <g
            transform={`rotate(${needleAngle}, 180, 220)`}
            opacity={compassDraw}
          >
            <polygon
              points="180,75 168,220 192,220"
              fill="#f87171"
              opacity="0.9"
            />
            <polygon
              points="180,365 168,220 192,220"
              fill="#64748b"
              opacity="0.7"
            />
            <circle cx="180" cy="220" r="8" fill="#ecfccb" />
          </g>

          {/* Tick marks */}
          {Array.from({ length: 36 }).map((_, i) => {
            const angle = i * 10;
            const rad = (angle * Math.PI) / 180;
            const inner = i % 9 === 0 ? 105 : 114;
            return (
              <line
                key={i}
                x1={180 + inner * Math.cos(rad)}
                y1={220 + inner * Math.sin(rad)}
                x2={180 + 127 * Math.cos(rad)}
                y2={220 + 127 * Math.sin(rad)}
                stroke="#84cc16"
                strokeWidth={i % 9 === 0 ? 2.5 : 1.2}
                opacity={0.4 * compassDraw}
              />
            );
          })}

          {/* Rule of threes on the right */}
          {p.survivalRuleOfThrees.map((rule, i) => {
            const ruleS = spring({
              frame: frame - (compassStart + 30 + i * 12),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={ruleS}>
                <rect
                  x="370"
                  y={60 + i * 62}
                  width="200"
                  height="48"
                  rx="8"
                  fill="#1a2e05"
                  stroke="#3f621240"
                  strokeWidth="1"
                />
                <text
                  x="388"
                  y={91 + i * 62}
                  fill="#ecfccb"
                  fontSize="22"
                  fontWeight="600"
                >
                  {rule.label}
                </text>
                <text
                  x="554"
                  y={91 + i * 62}
                  fill="#84cc16"
                  fontSize="22"
                  fontWeight="700"
                  textAnchor="end"
                >
                  {rule.time}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Emergency Signals + Survival Facts ===== */}
      <div style={{ position: 'absolute', top: 130, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#7a8a60',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          紧急求救信号
        </div>
        {p.signals.map((sig, i) => {
          const s = spring({
            frame: frame - (signalStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1a2e0590',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1.5px solid ${sig.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: sig.color }}>
                  {sig.name}
                </span>
                <span style={{ fontSize: 22, color: '#7a8a60' }}>
                  有效距离 {sig.range}
                </span>
              </div>
              <div style={{ fontSize: 22, color: '#7a8a60', marginTop: 4 }}>
                方法: {sig.method}
              </div>
            </div>
          );
        })}

        {/* Survival Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#7a8a60',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            生存必备技能
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.survivalFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#1a2e0590',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #3f621240',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{fact.icon}</div>
                  <div
                    style={{ fontSize: 24, fontWeight: 700, color: '#84cc16' }}
                  >
                    {fact.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a2e0590',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #3f621240',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{ fontSize: 34, fontWeight: 900, color: '#84cc16' }}
                >
                  {st.value}
                </span>
                <span style={{ fontSize: 22, color: '#7a8a60' }}>
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1a2e0590',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #3f6212',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🔥</span>
          <span style={{ fontSize: 22, color: '#ecfccb', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #3f6212, #84cc16, #ecfccb)',
        }}
      />
    </AbsoluteFill>
  );
};
